import { Service, computed, inject } from '@angular/core';
import { slugDeServico } from '../../../shared/utils/slug.util';
import {
  CategoriaServicosCor,
  CategoriaServicosItem,
  CategoriaServicosTipo,
} from '../models/categoria-servicos.model';
import {
  CategoriaComServicosPublicaResponse,
  ServicoPublicoResponse,
} from '../models/todos-servicos.model';
import { CatalogoPublicoService } from './catalogo-publico.service';
import { corDaCategoria, svgDaCategoria } from './identidade-categoria.util';

const ICONES = 'assets/images/icones-servicos';

/**
 * O tipo de atendimento que faz de um serviço um serviço DIGITAL — decisão de 29/09/2026,
 * o mesmo de-para que o adm declara em `ATENDIMENTO_POR_TIPO` (`Digital` → `Solicitação
 * Online`).
 *
 * Comparado pelo rótulo exato: é `TipoAtendimentoServico.TipoSolicitacaoOnline` no
 * back-end, um conjunto fechado pelo CHECK de `des_tipo_tas`. Se o rótulo mudar lá, esta
 * tela fica vazia sem erro — por isso ele vive num lugar só.
 */
const TIPO_SOLICITACAO_ONLINE = 'Solicitação Online';

/** O serviço digital que também atende no balcão ganha a etiqueta "Presencial". */
const TIPO_ATENDIMENTO_PRESENCIAL = 'Atendimento Presencial';

const DESCRICAO_AUSENTE = 'Descrição não informada.';

/**
 * Ícone de card para categoria sem chave, ou com uma fora do catálogo do adm. A tradução
 * das chaves conhecidas é a de `svgDaCategoria`, compartilhada com a tela de categorias.
 */
const ICONE_PADRAO = `${ICONES}/icon-servicos-digitais.svg`;

/**
 * Um serviço digital: o card, mais as categorias a que ele pertence.
 *
 * Estende o item de categoria para ir direto ao card compartilhado sem alterá-lo. O ícone e
 * a cor são os da **categoria principal** — a primeira, na ordem editorial, em que o
 * serviço aparece —, porque o serviço em si não tem nem um nem outro.
 */
export interface ServicoDigitalItem extends CategoriaServicosItem {
  /** Todas as categorias do serviço (`codCategoriaServicoCtg` em texto), para o filtro. */
  readonly categoriaIds: readonly string[];
}

/** Uma opção de "Filtrar por categoria". */
export interface CategoriaServicoDigital {
  /** O `codCategoriaServicoCtg` em texto. */
  readonly id: string;
  readonly titulo: string;
  readonly cor: CategoriaServicosCor;
  /** Serviços digitais da categoria — o serviço em duas categorias conta nas duas. */
  readonly total: number;
}

/**
 * Os serviços digitais do órgão, de todas as categorias — a tela `/servicos-digitais`.
 *
 * Não faz chamada própria: deriva dos grupos do `CatalogoPublicoService` (`GET
 * publico/servico`). A leitura é a mesma de que a página do serviço depende para resolver
 * o slug, então o clique no card não carrega o catálogo de novo.
 *
 * Carga, falha e nova tentativa dependem **só** dos grupos: o menu de categorias do
 * catálogo não entra nesta tela, e a falha dele não deve esvaziá-la.
 */
@Service()
export class ServicosDigitaisService {
  private readonly catalogo = inject(CatalogoPublicoService);

  readonly carregando = this.catalogo.carregandoServicos;

  readonly falhou = this.catalogo.falhouServicos;

  /**
   * Um item por serviço digital, sem repetição.
   *
   * A ordem é a do primeiro grupo em que o serviço aparece (ordem editorial das categorias)
   * e, dentro dele, a da API (nome). O serviço em duas categorias entra uma vez, com as
   * duas em `categoriaIds`.
   */
  readonly servicosDigitais = computed<readonly ServicoDigitalItem[]>(() => {
    const itens = new Map<number, { item: ServicoDigitalItem; categoriaIds: string[] }>();

    for (const grupo of this.catalogo.grupos()) {
      const categoriaId = String(grupo.codCategoriaServicoCtg);

      for (const servico of grupo.servicos.filter(ehDigital)) {
        const existente = itens.get(servico.codServicoSrv);

        if (existente) {
          existente.categoriaIds.push(categoriaId);
          continue;
        }

        const categoriaIds = [categoriaId];
        const item = paraItem(servico, grupo, categoriaIds);

        itens.set(servico.codServicoSrv, { item, categoriaIds });
      }
    }

    return [...itens.values()].map(({ item }) => item);
  });

  /** As categorias com ao menos um serviço digital, na ordem editorial. */
  readonly categorias = computed<readonly CategoriaServicoDigital[]>(() =>
    this.catalogo
      .grupos()
      .map((grupo) => ({
        id: String(grupo.codCategoriaServicoCtg),
        titulo: grupo.desNomeCtg,
        cor: corDaCategoria(grupo.desCorTemaCtg),
        total: grupo.servicos.filter(ehDigital).length,
      }))
      .filter((categoria) => categoria.total > 0)
  );

  /** Refaz só a chamada dos grupos — o "Tentar novamente" da tela. */
  recarregar(): void {
    this.catalogo.recarregarServicos();
  }
}

function ehDigital(servico: ServicoPublicoResponse): boolean {
  return (servico.tiposAtendimento ?? []).includes(TIPO_SOLICITACAO_ONLINE);
}

/**
 * O serviço vira card. `categoriaIds` é a MESMA lista que o chamador continua preenchendo
 * com as próximas categorias do serviço — por isso é recebida, e não criada aqui.
 */
function paraItem(
  servico: ServicoPublicoResponse,
  categoria: CategoriaComServicosPublicaResponse,
  categoriaIds: readonly string[]
): ServicoDigitalItem {
  return {
    id: String(servico.codServicoSrv),
    titulo: servico.desNomeSrv,
    descricao: servico.desResumoSrv?.trim() || DESCRICAO_AUSENTE,
    cor: corDaCategoria(categoria.desCorTemaCtg),
    tipos: tiposDoCard(servico.tiposAtendimento ?? []),
    icone: svgDaCategoria(categoria.desIconeCtg, ICONE_PADRAO),
    // O slug do nome é a rota da página do serviço — a mesma regra do "Acesse aqui" de
    // "Todos os Serviços", que o `servicoExisteGuard` já reconhece.
    rotaPagina: slugDeServico(servico.desNomeSrv),
    categoriaIds,
  };
}

/**
 * Todo item é digital; o presencial entra quando o serviço também atende no balcão.
 * `Informativo` e `Agendamento` não têm etiqueta no card.
 */
function tiposDoCard(tiposAtendimento: readonly string[]): readonly CategoriaServicosTipo[] {
  return tiposAtendimento.includes(TIPO_ATENDIMENTO_PRESENCIAL)
    ? ['presencial', 'digital']
    : ['digital'];
}
